import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchJson, getAuthEventName, readStoredUser, refreshStoredUser } from '../../utils/auth';

const styles = {
  page: {
    minHeight: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    padding: '24px',
    boxSizing: 'border-box',
    background: 'radial-gradient(circle at top, rgba(179, 137, 77, 0.26), transparent 35%), linear-gradient(180deg, #3a2918 0%, #2b1e12 55%, #1d140d 100%)',
    color: '#f6edd7',
    fontFamily: '"Trebuchet MS", sans-serif',
    textAlign: 'center',
  },
  backButton: {
    position: 'absolute',
    top: '8px',
    left: '12px',
    zIndex: 1000,
    minWidth: '104px',
    padding: '9px 14px',
    border: '1px solid rgba(217, 187, 121, 0.45)',
    borderRadius: '6px',
    background: 'rgba(47, 36, 23, 0.92)',
    color: '#f6edd7',
    cursor: 'pointer',
    fontFamily: '"Trebuchet MS", sans-serif',
    fontSize: '0.92rem',
    fontWeight: 700,
    boxSizing: 'border-box',
  },
  titleBox: {
    minWidth: '374px',
    maxWidth: '100%',
    padding: '14px 18px',
    border: '1px solid rgba(92, 69, 36, 0.35)',
    borderRadius: '8px',
    background: 'linear-gradient(135deg, rgba(255, 247, 226, 0.98), rgba(226, 208, 168, 0.96))',
    color: '#2f2417',
    fontFamily: 'Georgia, serif',
    fontSize: '1.35rem',
    fontWeight: 700,
    boxSizing: 'border-box',
    boxShadow: '0 16px 40px rgba(29, 20, 13, 0.4)',
  },
  actions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(180px, 1fr))',
    gap: '14px',
    width: 'min(420px, 100%)',
    padding: '18px',
    border: '1px solid rgba(217, 187, 121, 0.35)',
    borderRadius: '8px',
    background: 'rgba(47, 36, 23, 0.92)',
    boxShadow: '0 16px 40px rgba(29, 20, 13, 0.4)',
  },
  button: {
    width: '100%',
    padding: '12px 18px',
    border: '1px solid #b3894d',
    borderRadius: '6px',
    background: 'rgba(248, 239, 217, 0.94)',
    color: '#2f2417',
    fontFamily: '"Trebuchet MS", sans-serif',
    fontSize: '1rem',
    fontWeight: 700,
    textDecoration: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  disabledButton: {
    opacity: 0.65,
    cursor: 'not-allowed',
  },
  gearButton: {
    position: 'absolute',
    top: '8px',
    right: '12px',
    zIndex: 1000,
    width: '44px',
    height: '44px',
    border: '1px solid rgba(217, 187, 121, 0.45)',
    borderRadius: '6px',
    background: 'rgba(47, 36, 23, 0.92)',
    color: '#f6edd7',
    cursor: 'pointer',
    fontSize: '22px',
    lineHeight: 1,
    boxSizing: 'border-box',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    background: 'rgba(10, 6, 3, 0.72)',
  },
  modal: {
    width: 'min(920px, 100%)',
    maxHeight: '82dvh',
    overflow: 'auto',
    padding: '20px',
    border: '1px solid rgba(217, 187, 121, 0.35)',
    borderRadius: '8px',
    background: 'linear-gradient(180deg, rgba(58, 41, 24, 0.98), rgba(29, 20, 13, 0.98))',
    color: '#f6edd7',
    boxShadow: '0 18px 42px rgba(4, 11, 16, 0.34)',
    textAlign: 'left',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '14px',
  },
  patronRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(180px, 1fr) minmax(110px, auto) minmax(360px, auto)',
    gap: '12px',
    alignItems: 'center',
    padding: '10px 0',
    borderTop: '1px solid rgba(217, 187, 121, 0.18)',
  },
  roleActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  smallButton: {
    padding: '7px 10px',
    border: '1px solid #b3894d',
    borderRadius: '6px',
    background: 'rgba(248, 239, 217, 0.94)',
    color: '#2f2417',
    fontWeight: 700,
    cursor: 'pointer',
  },
  mutedText: {
    color: 'rgba(246, 237, 215, 0.72)',
    fontSize: '0.9rem',
  },
};

function formatRole(role = 'user') {
  const normalizedRole = String(role).trim().toLowerCase();
  if (normalizedRole === 'owner') return 'Owner';
  if (normalizedRole === 'admin') return 'Admin';
  if (normalizedRole === 'writer') return 'Writer';
  if (normalizedRole === 'traveler') return 'Traveler';
  return 'Viewer';
}

function canManagePirateRoles(access) {
  return Boolean(access?.canManageRoles);
}

function getAssignableRoles(access) {
  return access?.role === 'owner'
    ? ['viewer', 'traveler', 'admin', 'writer']
    : ['viewer', 'traveler'];
}

export default function Pirate() {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => readStoredUser());
  const [pirateAccess, setPirateAccess] = useState({ role: 'viewer', canManageRoles: false });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [patrons, setPatrons] = useState([]);
  const [settingsMessage, setSettingsMessage] = useState('');
  const [isPatronsBusy, setIsPatronsBusy] = useState(false);
  const assignableRoles = getAssignableRoles(pirateAccess);

  useEffect(() => {
    let ignore = false;

    async function syncUser() {
      const storedUser = readStoredUser();
      setUser(storedUser);

      if (!storedUser) {
        setPirateAccess({ role: 'viewer', canManageRoles: false });
        return;
      }

      try {
        await refreshStoredUser();
        const { response, payload } = await fetchJson('/pirate-system/access', {
          method: 'GET',
        });

        if (!ignore && response.ok) {
          setPirateAccess(payload.access || { role: 'viewer', canManageRoles: false });
        }
      } catch (_error) {
        if (!ignore) {
          setPirateAccess({ role: 'viewer', canManageRoles: false });
        }
      }
    }

    window.addEventListener(getAuthEventName(), syncUser);
    syncUser();

    return () => {
      ignore = true;
      window.removeEventListener(getAuthEventName(), syncUser);
    };
  }, []);

  async function loadPatrons() {
    if (!canManagePirateRoles(pirateAccess)) return;

    setIsPatronsBusy(true);
    setSettingsMessage('');

    try {
      const { response, payload } = await fetchJson('/pirate-system/roles/list', {
        method: 'POST',
      });

      if (!response.ok) {
        setSettingsMessage(payload.message || 'Unable to load users.');
        return;
      }

      setPatrons(payload.patrons || []);
    } catch (_error) {
      setSettingsMessage('Unable to load users.');
    } finally {
      setIsPatronsBusy(false);
    }
  }

  function openSettings() {
    setIsSettingsOpen(true);
    loadPatrons();
  }

  async function handleRoleChange(patron, nextRole) {
    setIsPatronsBusy(true);
    setSettingsMessage('');

    try {
      const { response, payload } = await fetchJson('/pirate-system/roles/set-role', {
        method: 'POST',
        body: JSON.stringify({
          targetUserId: patron.id,
          role: nextRole,
        }),
      });

      if (!response.ok) {
        setSettingsMessage(payload.message || 'Unable to update that user.');
        return;
      }

      setPatrons((prev) => prev.map((item) => (item.id === payload.patron.id ? payload.patron : item)));
      setSettingsMessage(payload.message || 'User role updated.');

      if (payload.patron.id === user?.id) {
        const { response: accessResponse, payload: accessPayload } = await fetchJson('/pirate-system/access', {
          method: 'GET',
        });

        if (accessResponse.ok) {
          setPirateAccess(accessPayload.access || { role: 'viewer', canManageRoles: false });
        }
      }
    } catch (_error) {
      setSettingsMessage('Unable to update that user.');
    } finally {
      setIsPatronsBusy(false);
    }
  }

  return (
    <main style={styles.page}>
      <button type="button" onClick={() => navigate('/projects')} style={styles.backButton}>
        &lt; Back
      </button>
      {canManagePirateRoles(pirateAccess) && (
        <button
          type="button"
          onClick={openSettings}
          style={styles.gearButton}
          aria-label="Open Pirate settings"
          title="Pirate settings"
        >
          &#9881;
        </button>
      )}
      <div style={styles.titleBox}>Pirate TTRPG</div>
      <div style={styles.actions}>
        <Link to="/projects/PirateCompendium" style={styles.button}>
          Compendium
        </Link>
        <Link to="/projects/PirateCharacterSheet" style={styles.button}>
          Character Sheet
        </Link>
        <Link to="/projects/PirateNpcBuilder" style={styles.button}>
          NPC Builder
        </Link>
        <Link to="/projects/PirateCustom" style={styles.button}>
          Homebrew Builder
        </Link>
      </div>
      {isSettingsOpen && (
        <div style={styles.modalOverlay} onClick={() => setIsSettingsOpen(false)}>
          <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={{ margin: 0, fontFamily: 'Georgia, serif' }}>Pirate Settings</h2>
                <div style={styles.mutedText}>
                  Traveler, Admin, Writer, and Owner roles can use Pirate cloud sharing.
                </div>
              </div>
              <button type="button" style={styles.smallButton} onClick={() => setIsSettingsOpen(false)}>
                Close
              </button>
            </div>

            <div style={styles.mutedText}>
              {pirateAccess.role === 'owner'
                ? 'Owners can assign Viewer, Traveler, Admin, or Writer roles.'
                : 'Pirate admins and writers can assign Viewer or Traveler roles.'}
            </div>

            {settingsMessage && (
              <div style={{ marginTop: '12px', color: '#d9bb79', fontWeight: 700 }}>{settingsMessage}</div>
            )}

            <div style={{ marginTop: '12px' }}>
              {isPatronsBusy && patrons.length === 0 ? (
                <div>Loading users...</div>
              ) : (
                patrons.map((patron) => (
                  <div key={patron.id} style={styles.patronRow}>
                    <div>
                      <div style={{ fontWeight: 700 }}>{patron.username || 'Unnamed user'}</div>
                      <div style={styles.mutedText}>{patron.email}</div>
                    </div>
                    <div>{formatRole(patron.pirateRole)}</div>
                    <div style={styles.roleActions}>
                      {assignableRoles.map((role) => (
                        <button
                          key={role}
                          type="button"
                          style={{
                            ...styles.smallButton,
                            opacity: isPatronsBusy || patron.pirateRole === role ? 0.62 : 1,
                          }}
                          onClick={() => handleRoleChange(patron, role)}
                          disabled={isPatronsBusy || patron.pirateRole === role || patron.pirateRole === 'owner'}
                        >
                          {formatRole(role)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
